import { AccessRoleCodes } from "Models/AccessRoleCodes";

/**
 * Whether a Lite viewer may pay a request posted by the given persona (App ADR-0013). A Lite
 * account is a restricted debtor: it may pay merchants, never other customers — the same rule the
 * API enforces at pay time, mirrored here so the card explains instead of failing.
 */
export const canLiteViewerPay = (creditorAccessRole: string | undefined) =>
  creditorAccessRole === AccessRoleCodes.Merchant;

/**
 * Whether a Full Moone viewer may send a direct payment to the counterpart: a Lite counterpart
 * cannot receive (App ADR-0013). An unknown tier (a Stream profile written before the field
 * existed) keeps the action, the API decides.
 */
export const canSendDirectPaymentTo = (
  isCounterpartFullMoone: boolean | undefined,
) => isCounterpartFullMoone !== false;

/**
 * Whether a "pay me" request makes sense toward the counterpart: they must be able to pay it.
 * A Lite counterpart can pay merchants only, so a Merchant persona keeps the request toward
 * them while a customer does not. Unknown tier keeps the action, as above.
 */
export const canRequestPaymentFrom = (
  viewerAccessRole: string | null | undefined,
  isCounterpartFullMoone: boolean | undefined,
) =>
  isCounterpartFullMoone !== false ||
  viewerAccessRole === AccessRoleCodes.Merchant;

type ChatPaymentMenuInput = {
  isGroup: boolean;
  /** A direct channel whose counterpart could be resolved — the only place a direct payment goes. */
  isOneOnOneChat: boolean;
  isViewerFullMoone: boolean;
  viewerAccessRole: string | null | undefined;
  /** The counterpart's tier as the Stream profile carries it; undefined = unknown, the API decides. */
  isCounterpartFullMoone: boolean | undefined;
};

export type ChatPaymentMenu =
  /** A Lite viewer has no menu action at all: they can neither send nor receive. */
  | { kind: "viewerRestricted" }
  /** A Full Moone viewer, but nothing is possible toward this counterpart. */
  | { kind: "counterpartRestricted" }
  | {
      kind: "menu";
      isDirectPaymentAvailable: boolean;
      isPaymentRequestAvailable: boolean;
    };

/**
 * What the composer's payment menu shows (App ADR-0013). A Lite viewer sees their own
 * restriction whatever the chat; what they may still do is pay a merchant's request card
 * (canLiteViewerPay). A Full Moone viewer gets the menu per item: "send payment" needs a
 * counterpart who can receive, "pay me" needs one who can pay it — in a group always, since the
 * multi-payer request is paid by whoever can. When neither item survives, the counterpart's
 * restriction is shown instead of an empty menu.
 */
export const resolveChatPaymentMenu = ({
  isGroup,
  isOneOnOneChat,
  isViewerFullMoone,
  viewerAccessRole,
  isCounterpartFullMoone,
}: ChatPaymentMenuInput): ChatPaymentMenu => {
  if (!isViewerFullMoone) {
    return { kind: "viewerRestricted" };
  }

  const isDirectPaymentAvailable =
    isOneOnOneChat && canSendDirectPaymentTo(isCounterpartFullMoone);
  const isPaymentRequestAvailable =
    isGroup || canRequestPaymentFrom(viewerAccessRole, isCounterpartFullMoone);

  if (!isDirectPaymentAvailable && !isPaymentRequestAvailable) {
    return { kind: "counterpartRestricted" };
  }

  return { kind: "menu", isDirectPaymentAvailable, isPaymentRequestAvailable };
};
